import React, { useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Icon from 'jitsi/base/icons/components/Icon';
import { IconArrowDown } from 'jitsi/base/icons/svg';
import { withPixelLineHeight } from 'jitsi/base/styles/functions.web';
const useStyles = makeStyles()((theme, _, classes) => {
    return {
        actionButton: {
            ...withPixelLineHeight(theme.typography.bodyLongBold),
            fontSize: 15,
            borderRadius: theme.shape.borderRadius,
            boxSizing: 'border-box',
            color: theme.palette.text01,
            cursor: 'pointer',
            display: 'inline-block',
            marginBottom: '16px',
            padding: '7px 16px',
            position: 'relative',
            textAlign: 'center',
            height: 40,
            width: '100%',
            border: '1px solid',
            '&.primary': {
                background: theme.palette.iisightBrand,
                borderColor: theme.palette.iisightBrand,
                color: theme.palette.text01,
                [`&:not(.with-options):hover`]: {
                    backgroundColor: theme.palette.iisightBrand900,
                    borderColor: theme.palette.iisightBrand900,
                },
            },
            '&.secondary': {
                backgroundColor: theme.palette.iisightBlue500,
                borderColor: theme.palette.iisightBlue500,
                color: theme.palette.iisightWhiteText,
                [`&:not(.with-options):hover`]: {
                    backgroundColor: theme.palette.iisightBlue700,
                    borderColor: theme.palette.iisightBlue700,
                },
                '&:active': {
                    backgroundColor: theme.palette.iisightBlue900,
                    borderColor: theme.palette.iisightBlue900,
                },
            },
            '&.text': {
                width: 'auto',
                fontSize: '13px',
                margin: '0',
                padding: '0',
            },
            '&.disabled': {
                background: theme.palette.disabled01,
                border: '1px solid #5E6D7A',
                color: '#AFB6BC',
                cursor: 'initial',
                '.icon': {
                    '& > svg': {
                        fill: '#AFB6BC',
                    },
                },
            },
            [theme.breakpoints.down(400)]: {
                fontSize: 16,
                marginBottom: 8,
                padding: '11px 16px',
            },
        },
        options: {
            borderRadius: Number(theme.shape.borderRadius) / 2,
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            position: 'absolute',
            right: 0,
            top: 0,
            width: 36,
            [`&:not(.disabled):hover`]: {
                backgroundColor: theme.palette.iisightBrand900,
            },
            '& svg': {
                pointerEvents: 'none',
            },
        },
    };
});
/**
 * Button used for pre meeting actions.
 *
 * @returns {ReactElement}
 */
function ActionButton({ children, className = '', disabled, hasOptions, OptionsIcon = IconArrowDown, testId, type = 'primary', onClick, onOptionsClick, tabIndex, role, ariaPressed, ariaLabel, ariaDropDownLabel, style, }) {
    const { classes, cx } = useStyles();
    const onKeyPressHandler = useCallback((e) => {
        if (onClick && !disabled && (e.key === ' ' || e.key === 'Enter')) {
            e.preventDefault();
            onClick(e);
        }
    }, [onClick, disabled]);
    const onOptionsKeyPressHandler = useCallback((e) => {
        if (onOptionsClick && !disabled && (e.key === ' ' || e.key === 'Enter')) {
            e.preventDefault();
            e.stopPropagation();
            onOptionsClick(e);
        }
    }, [onOptionsClick, disabled]);
    const containerClasses = cx(classes.actionButton, hasOptions && "with-options", className && className, 
    // 11sight added line
    'action-btn', type, disabled && 'disabled');
    return (React.createElement("div", { "aria-disabled": disabled, "aria-label": ariaLabel, className: containerClasses, "data-testid": testId ? testId : undefined, onClick: disabled ? undefined : onClick, onKeyPress: onKeyPressHandler, role: 'button', tabIndex: 0, style: style },
        children,
        hasOptions && (React.createElement("div", { "aria-disabled": disabled, "aria-haspopup": 'true', "aria-label": ariaDropDownLabel, "aria-pressed": ariaPressed, 
            // 11sight modified
            className: cx(classes.options, 'options', disabled && 'disabled'), "data-testid": 'prejoin.joinOptions', onClick: disabled ? undefined : onOptionsClick, onKeyPress: onOptionsKeyPressHandler, role: role, tabIndex: tabIndex },
            React.createElement(Icon, { className: 'icon', size: 24, src: OptionsIcon })))));
}
export default ActionButton;
